/**
 * Notification Redux Types
 * Type definitions for notification state management
 */

// Action Types
export const FETCH_NOTIFICATIONS_REQUEST = 'NOTIFICATION_FETCH_NOTIFICATIONS_REQUEST';
export const FETCH_NOTIFICATIONS_SUCCESS = 'NOTIFICATION_FETCH_NOTIFICATIONS_SUCCESS';
export const FETCH_NOTIFICATIONS_FAILURE = 'NOTIFICATION_FETCH_NOTIFICATIONS_FAILURE';

export const FETCH_UNREAD_COUNT_REQUEST = 'NOTIFICATION_FETCH_UNREAD_COUNT_REQUEST';
export const FETCH_UNREAD_COUNT_SUCCESS = 'NOTIFICATION_FETCH_UNREAD_COUNT_SUCCESS';
export const FETCH_UNREAD_COUNT_FAILURE = 'NOTIFICATION_FETCH_UNREAD_COUNT_FAILURE';

export const MARK_AS_READ_REQUEST = 'NOTIFICATION_MARK_AS_READ_REQUEST';
export const MARK_AS_READ_SUCCESS = 'NOTIFICATION_MARK_AS_READ_SUCCESS';
export const MARK_AS_READ_FAILURE = 'NOTIFICATION_MARK_AS_READ_FAILURE';

export const MARK_ALL_AS_READ_REQUEST = 'NOTIFICATION_MARK_ALL_AS_READ_REQUEST';
export const MARK_ALL_AS_READ_SUCCESS = 'NOTIFICATION_MARK_ALL_AS_READ_SUCCESS';
export const MARK_ALL_AS_READ_FAILURE = 'NOTIFICATION_MARK_ALL_AS_READ_FAILURE';

export const DELETE_NOTIFICATION_REQUEST = 'NOTIFICATION_DELETE_NOTIFICATION_REQUEST';
export const DELETE_NOTIFICATION_SUCCESS = 'NOTIFICATION_DELETE_NOTIFICATION_SUCCESS';
export const DELETE_NOTIFICATION_FAILURE = 'NOTIFICATION_DELETE_NOTIFICATION_FAILURE';

export const FETCH_PREFERENCES_REQUEST = 'NOTIFICATION_FETCH_PREFERENCES_REQUEST';
export const FETCH_PREFERENCES_SUCCESS = 'NOTIFICATION_FETCH_PREFERENCES_SUCCESS';
export const FETCH_PREFERENCES_FAILURE = 'NOTIFICATION_FETCH_PREFERENCES_FAILURE';

export const UPDATE_PREFERENCES_REQUEST = 'NOTIFICATION_UPDATE_PREFERENCES_REQUEST';
export const UPDATE_PREFERENCES_SUCCESS = 'NOTIFICATION_UPDATE_PREFERENCES_SUCCESS';
export const UPDATE_PREFERENCES_FAILURE = 'NOTIFICATION_UPDATE_PREFERENCES_FAILURE';

export const ADD_NOTIFICATION = 'NOTIFICATION_ADD_NOTIFICATION';
export const REMOVE_NOTIFICATION = 'NOTIFICATION_REMOVE_NOTIFICATION';
export const CLEAR_NOTIFICATIONS = 'NOTIFICATION_CLEAR_NOTIFICATIONS';
export const INCREMENT_UNREAD_COUNT = 'NOTIFICATION_INCREMENT_UNREAD_COUNT';
export const DECREMENT_UNREAD_COUNT = 'NOTIFICATION_DECREMENT_UNREAD_COUNT';

// State Interfaces

export interface NotificationItem {
  _id: string;
  type: string;
  category: string;
  title: string;
  message: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'sent' | 'delivered' | 'read' | 'failed';
  isRead: boolean;
  createdAt: string;
  readAt?: string;
  data?: Record<string, any>;
  actionUrl?: string;
  channel?: 'push' | 'inApp' | 'email';
}

export interface NotificationPreferences {
  enabled: boolean;
  channels: {
    push: boolean;
    inApp: boolean;
    email: boolean;
  };
  categories: {
    [key: string]: {
      enabled: boolean;
      channels: {
        push: boolean;
        inApp: boolean;
        email: boolean;
      };
    };
  };
  quietHours?: {
    enabled: boolean;
    start: string; // HH:mm format
    end: string; // HH:mm format
  };
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface NotificationState {
  notifications: NotificationItem[];
  unreadCount: number;
  preferences: NotificationPreferences | null;
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo;
  lastFetched: number | null;
}

// Action Interfaces

export interface FetchNotificationsRequestAction {
  type: typeof FETCH_NOTIFICATIONS_REQUEST;
  payload: {
    page?: number;
    limit?: number;
    category?: string;
  };
}

export interface FetchNotificationsSuccessAction {
  type: typeof FETCH_NOTIFICATIONS_SUCCESS;
  payload: {
    notifications: NotificationItem[];
    pagination: PaginationInfo;
  };
}

export interface FetchNotificationsFailureAction {
  type: typeof FETCH_NOTIFICATIONS_FAILURE;
  payload: string;
}

export interface FetchUnreadCountRequestAction {
  type: typeof FETCH_UNREAD_COUNT_REQUEST;
}

export interface FetchUnreadCountSuccessAction {
  type: typeof FETCH_UNREAD_COUNT_SUCCESS;
  payload: number;
}

export interface FetchUnreadCountFailureAction {
  type: typeof FETCH_UNREAD_COUNT_FAILURE;
  payload: string;
}

export interface MarkAsReadRequestAction {
  type: typeof MARK_AS_READ_REQUEST;
  payload: string; // notification ID
}

export interface MarkAsReadSuccessAction {
  type: typeof MARK_AS_READ_SUCCESS;
  payload: string; // notification ID
}

export interface MarkAsReadFailureAction {
  type: typeof MARK_AS_READ_FAILURE;
  payload: string;
}

export interface MarkAllAsReadRequestAction {
  type: typeof MARK_ALL_AS_READ_REQUEST;
}

export interface MarkAllAsReadSuccessAction {
  type: typeof MARK_ALL_AS_READ_SUCCESS;
}

export interface MarkAllAsReadFailureAction {
  type: typeof MARK_ALL_AS_READ_FAILURE;
  payload: string;
}

export interface DeleteNotificationRequestAction {
  type: typeof DELETE_NOTIFICATION_REQUEST;
  payload: string; // notification ID
}

export interface DeleteNotificationSuccessAction {
  type: typeof DELETE_NOTIFICATION_SUCCESS;
  payload: string; // notification ID
}

export interface DeleteNotificationFailureAction {
  type: typeof DELETE_NOTIFICATION_FAILURE;
  payload: string;
}

export interface FetchPreferencesRequestAction {
  type: typeof FETCH_PREFERENCES_REQUEST;
}

export interface FetchPreferencesSuccessAction {
  type: typeof FETCH_PREFERENCES_SUCCESS;
  payload: NotificationPreferences;
}

export interface FetchPreferencesFailureAction {
  type: typeof FETCH_PREFERENCES_FAILURE;
  payload: string;
}

export interface UpdatePreferencesRequestAction {
  type: typeof UPDATE_PREFERENCES_REQUEST;
  payload: Partial<NotificationPreferences>;
}

export interface UpdatePreferencesSuccessAction {
  type: typeof UPDATE_PREFERENCES_SUCCESS;
  payload: NotificationPreferences;
}

export interface UpdatePreferencesFailureAction {
  type: typeof UPDATE_PREFERENCES_FAILURE;
  payload: string;
}

export interface AddNotificationAction {
  type: typeof ADD_NOTIFICATION;
  payload: NotificationItem;
}

export interface RemoveNotificationAction {
  type: typeof REMOVE_NOTIFICATION;
  payload: string; // notification ID
}

export interface ClearNotificationsAction {
  type: typeof CLEAR_NOTIFICATIONS;
}

export interface IncrementUnreadCountAction {
  type: typeof INCREMENT_UNREAD_COUNT;
}

export interface DecrementUnreadCountAction {
  type: typeof DECREMENT_UNREAD_COUNT;
}

// Combined Action Types
export type NotificationActionTypes =
  | FetchNotificationsRequestAction
  | FetchNotificationsSuccessAction
  | FetchNotificationsFailureAction
  | FetchUnreadCountRequestAction
  | FetchUnreadCountSuccessAction
  | FetchUnreadCountFailureAction
  | MarkAsReadRequestAction
  | MarkAsReadSuccessAction
  | MarkAsReadFailureAction
  | MarkAllAsReadRequestAction
  | MarkAllAsReadSuccessAction
  | MarkAllAsReadFailureAction
  | DeleteNotificationRequestAction
  | DeleteNotificationSuccessAction
  | DeleteNotificationFailureAction
  | FetchPreferencesRequestAction
  | FetchPreferencesSuccessAction
  | FetchPreferencesFailureAction
  | UpdatePreferencesRequestAction
  | UpdatePreferencesSuccessAction
  | UpdatePreferencesFailureAction
  | AddNotificationAction
  | RemoveNotificationAction
  | ClearNotificationsAction
  | IncrementUnreadCountAction
  | DecrementUnreadCountAction;
